import React, { useState, useEffect } from 'react';
import {
    connectGoogleCalendar,
    disconnectGoogleCalendar,
    getCalendarStatus,
    getMeetingSettings,
    updateMeetingSettings,
} from '../../services/api';
import { useNotification } from '../../context/NotificationContext';
import { Calendar, Clock, Timer, CalendarRange, Globe, Check, Unplug } from 'lucide-react';

const DURATIONS = [15, 30, 45, 60];
const BUFFER_OPTIONS = [0, 5, 10, 15, 30];
const WINDOW_OPTIONS = [7, 10, 14, 20, 30];

const TIMEZONES = [
    'Asia/Kolkata',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Berlin',
    'Asia/Dubai',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
];

export default function CalendarSettings() {
    const { showSuccess, showError } = useNotification();
    const [connected, setConnected] = useState(false);
    const [connectedAt, setConnectedAt] = useState(null);
    const [connectedEmail, setConnectedEmail] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        default_duration_minutes: 30,
        allowed_durations: [30],
        buffer_before_minutes: 15,
        buffer_after_minutes: 10,
        available_hours: { start: '09:00', end: '18:00' },
        booking_window_days: 14,
        timezone: 'Asia/Kolkata',
    });

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        const handleMessage = (event) => {
            const apiUrl = process.env.REACT_APP_API_URL || `${window.location.origin}/api/v1`;
            const expectedOrigin = new URL(apiUrl).origin;
            if (event.origin !== expectedOrigin && event.origin !== window.location.origin) {
                return;
            }

            if (event.data?.provider !== 'google_calendar') {
                return;
            }

            if (event.data.type === 'oauth_success' && event.data.status === 'connected') {
                showSuccess('Google Calendar connected successfully.');
                loadData();
            } else if (event.data.type === 'oauth_error') {
                showError('Google Calendar connection failed. Please try again.');
                loadData();
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [showError, showSuccess]);

    const loadData = async () => {
        setLoading(true);
        try {
            const [statusRes, settingsRes] = await Promise.allSettled([
                getCalendarStatus(),
                getMeetingSettings(),
            ]);
            if (statusRes.status === 'fulfilled') {
                setConnected(statusRes.value.data.connected);
                setConnectedAt(statusRes.value.data.connected_at);
                setConnectedEmail(statusRes.value.data.email || '');
            }
            if (settingsRes.status === 'fulfilled') {
                setSettings(settingsRes.value.data);
            }
        } catch (err) {
            console.error('Failed to load calendar settings', err);
            showError('Failed to load Google Calendar settings.');
        } finally {
            setLoading(false);
        }
    };

    const handleConnect = async () => {
        try {
            const { data } = await connectGoogleCalendar();
            const width = 620;
            const height = 760;
            const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
            const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
            const popup = window.open(
                data.redirect_url,
                'google-calendar-oauth',
                `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`,
            );

            if (!popup || popup.closed || typeof popup.closed === 'undefined') {
                showError('Popup blocked! Please allow popups for this site to connect Google Calendar.');
                return;
            }

            const timer = window.setInterval(() => {
                if (popup.closed) {
                    window.clearInterval(timer);
                    setTimeout(() => {
                        loadData();
                    }, 600);
                }
            }, 500);
        } catch (err) {
            showError(err.response?.data?.detail || 'Failed to start Google connection');
        }
    };

    const handleDisconnect = async () => {
        if (!confirm('Disconnect Google Calendar? Meeting bookings will stop working.')) return;
        try {
            await disconnectGoogleCalendar();
            setConnected(false);
            setConnectedAt(null);
            setConnectedEmail('');
            showSuccess('Google Calendar disconnected.');
        } catch (err) {
            showError('Failed to disconnect.');
        }
    };

    const handleSave = async () => {
        if (!settings.allowed_durations?.length) {
            showError('Select at least one allowed duration.');
            return;
        }
        if (!settings.allowed_durations.includes(settings.default_duration_minutes)) {
            showError('Default duration must also be selected as an allowed duration.');
            return;
        }
        if ((settings.available_hours?.start || '') >= (settings.available_hours?.end || '')) {
            showError('Working hours start must be earlier than end.');
            return;
        }

        setSaving(true);
        try {
            await updateMeetingSettings({
                default_duration_minutes: settings.default_duration_minutes,
                allowed_durations: settings.allowed_durations,
                buffer_before_minutes: settings.buffer_before_minutes,
                buffer_after_minutes: settings.buffer_after_minutes,
                available_hours_start: settings.available_hours?.start,
                available_hours_end: settings.available_hours?.end,
                booking_window_days: settings.booking_window_days,
                timezone: settings.timezone,
            });
            showSuccess('Scheduler settings saved.');
        } catch (err) {
            showError(err.response?.data?.detail || 'Failed to save settings.');
        } finally {
            setSaving(false);
        }
    };

    const toggleDuration = (d) => {
        const current = settings.allowed_durations || [];
        const next = current.includes(d)
            ? current.filter(x => x !== d)
            : [...current, d].sort((a, b) => a - b);
        if (next.length === 0) return; // Must have at least one
        setSettings({
            ...settings,
            allowed_durations: next,
            default_duration_minutes: next.includes(settings.default_duration_minutes)
                ? settings.default_duration_minutes
                : next[0],
        });
    };

    if (loading) {
        return <div className="card padded" style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>Loading calendar settings...</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Connection Card */}
            <div className="card padded">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                            width: 44, height: 44, borderRadius: 12,
                            background: connected ? '#ecfdf5' : '#f3f4f6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: connected ? '#22c55e' : '#94a3b8',
                        }}>
                            <Calendar size={22} />
                        </div>
                        <div>
                            <div style={{ fontWeight: 700, fontSize: '1rem' }}>Google Calendar</div>
                            <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                                {connected
                                    ? `Connected${connectedAt ? ` · ${new Date(connectedAt).toLocaleDateString()}` : ''}`
                                    : 'Not connected — your AI agent needs this to book meetings'}
                            </div>
                            {connectedEmail ? (
                                <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                                    Connected account: {connectedEmail}
                                </div>
                            ) : null}
                        </div>
                    </div>
                    {connected ? (
                        <button className="btn" onClick={handleDisconnect} style={{ color: 'var(--danger)' }}>
                            <Unplug size={15} /> Disconnect
                        </button>
                    ) : (
                        <button className="btn primary" onClick={handleConnect}>
                            <Calendar size={15} /> Connect Calendar
                        </button>
                    )}
                </div>
            </div>

            {/* Availability Settings */}
            <div className="card padded">
                <h3 style={{ margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Clock size={18} /> Availability Settings
                </h3>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: '0 0 20px' }}>
                    Configure when your AI scheduler can offer meeting slots and how long meetings can be.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                    {/* Working Hours */}
                    <div>
                        <label style={{ fontWeight: 600, fontSize: '0.82rem', display: 'block', marginBottom: 6 }}>Working Hours Start</label>
                        <input
                            type="time"
                            className="input"
                            value={settings.available_hours?.start || '09:00'}
                            onChange={e => setSettings({
                                ...settings,
                                available_hours: { ...settings.available_hours, start: e.target.value },
                            })}
                        />
                    </div>
                    <div>
                        <label style={{ fontWeight: 600, fontSize: '0.82rem', display: 'block', marginBottom: 6 }}>Working Hours End</label>
                        <input
                            type="time"
                            className="input"
                            value={settings.available_hours?.end || '18:00'}
                            onChange={e => setSettings({
                                ...settings,
                                available_hours: { ...settings.available_hours, end: e.target.value },
                            })}
                        />
                    </div>

                    {/* Buffer Before */}
                    <div>
                        <label style={{ fontWeight: 600, fontSize: '0.82rem', display: 'block', marginBottom: 6 }}>
                            <Timer size={14} style={{ verticalAlign: '-2px' }} /> Buffer Before
                        </label>
                        <select
                            className="select"
                            value={settings.buffer_before_minutes}
                            onChange={e => setSettings({ ...settings, buffer_before_minutes: Number(e.target.value) })}
                        >
                            {BUFFER_OPTIONS.map(v => <option key={v} value={v}>{v === 0 ? 'None' : `${v} min`}</option>)}
                        </select>
                    </div>

                    {/* Buffer After */}
                    <div>
                        <label style={{ fontWeight: 600, fontSize: '0.82rem', display: 'block', marginBottom: 6 }}>
                            <Timer size={14} style={{ verticalAlign: '-2px' }} /> Buffer After
                        </label>
                        <select
                            className="select"
                            value={settings.buffer_after_minutes}
                            onChange={e => setSettings({ ...settings, buffer_after_minutes: Number(e.target.value) })}
                        >
                            {BUFFER_OPTIONS.map(v => <option key={v} value={v}>{v === 0 ? 'None' : `${v} min`}</option>)}
                        </select>
                    </div>

                    {/* Booking Window */}
                    <div>
                        <label style={{ fontWeight: 600, fontSize: '0.82rem', display: 'block', marginBottom: 6 }}>
                            <CalendarRange size={14} style={{ verticalAlign: '-2px' }} /> Booking Window
                        </label>
                        <select
                            className="select"
                            value={settings.booking_window_days}
                            onChange={e => setSettings({ ...settings, booking_window_days: Number(e.target.value) })}
                        >
                            {WINDOW_OPTIONS.map(v => <option key={v} value={v}>{v} days ahead</option>)}
                        </select>
                    </div>

                    {/* Timezone */}
                    <div>
                        <label style={{ fontWeight: 600, fontSize: '0.82rem', display: 'block', marginBottom: 6 }}>
                            <Globe size={14} style={{ verticalAlign: '-2px' }} /> Timezone
                        </label>
                        <select
                            className="select"
                            value={settings.timezone}
                            onChange={e => setSettings({ ...settings, timezone: e.target.value })}
                        >
                            {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                        </select>
                    </div>
                </div>

                {/* Allowed Durations */}
                <div style={{ marginTop: 20 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.82rem', display: 'block', marginBottom: 8 }}>
                        Allowed Meeting Durations
                    </label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {DURATIONS.map(d => {
                            const active = (settings.allowed_durations || []).includes(d);
                            return (
                                <button
                                    key={d}
                                    className={`btn ${active ? 'primary' : ''}`}
                                    onClick={() => toggleDuration(d)}
                                    style={{ minWidth: 80 }}
                                >
                                    {active && <Check size={14} />} {d} min
                                </button>
                            );
                        })}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 6 }}>
                        Only selected durations will be offered to customers.
                    </div>
                </div>

                <div style={{ marginTop: 20 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.82rem', display: 'block', marginBottom: 8 }}>
                        Default Duration
                    </label>
                    <select
                        className="select"
                        value={settings.default_duration_minutes}
                        onChange={e => setSettings({ ...settings, default_duration_minutes: Number(e.target.value) })}
                    >
                        {(settings.allowed_durations || []).map(duration => (
                            <option key={duration} value={duration}>{duration} min</option>
                        ))}
                    </select>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 6 }}>
                        Used when the customer does not specify a preferred meeting length.
                    </div>
                </div>

                <div style={{ marginTop: 20, padding: 14, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>How the AI scheduler works</div>
                    <div style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                        Customers can ask to book a meeting in WhatsApp. The AI will collect their preferred date, time, duration, and email, check your Google Calendar availability, and then create the event with an invite once they confirm.
                    </div>
                </div>

                <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn primary" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>
        </div>
    );
}
